import React, { ComponentProps, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ModalBase, notify, Spinner } from '@bynder/design-system';
import { GenAIModal, OriginalAsset } from '@bynder/image-gen-frontend-lib';
import { useTranslate } from '@bynder/localization';
import useAccessRights from 'packages/hooks/useAccessRights';
import { getBynderAccessToken, getPortalDomain } from 'packages/store/platform/platform.selectors';
import AssetsPicker from 'packages/pages/components/AssetsPickerModal/components/AssetsPicker';
import Sidebar from 'packages/pages/components/AssetsPickerModal/components/AssetsPickerSidebar';
import UploadButton from 'packages/pages/components/Uploader/UploadButton';
import useActions from 'packages/pages/components/AssetsPickerModal/components/useActions';
import { StyledModalBasedContent } from 'packages/pages/components/AssetsPickerModal/components/index.styled';
import { AssetGroup } from 'packages/pages/editor/ContentPickerModal/types';
import AssetsService from '~/services/AssetsService';
import features from '~/configs/features';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: ({ enhancementId, fileId }: { enhancementId: number; fileId: string }) => Promise<void>;
    sessionId: string;
    originalAsset: OriginalAsset;
    saveButtonLabel?: string;
}

export default function ImageGen({ isOpen, onClose, onSave, sessionId, originalAsset, saveButtonLabel }: Props) {
    const { isPlatformAllowed } = useAccessRights();
    const { translate } = useTranslate();
    const token = useSelector(getBynderAccessToken);
    const portalDomain = useSelector(getPortalDomain);
    type ShowAssetPicker = NonNullable<ComponentProps<typeof GenAIModal>['showAssetPicker']>;
    type AddAssetsCallback = Parameters<ShowAssetPicker>[0];
    type AssetPickerPayload = Parameters<AddAssetsCallback>[0];

    const [addAssetsCallback, setAddAssetsCallback] = useState<AddAssetsCallback | undefined>(undefined);
    const [isLocalAssetLoading, setIsLocalAssetLoading] = useState(false);

    const {
        assetType,
        wrapperRef,
        collectionId,
        selectedLocalAsset,
        allowInternalAsset,
        handleDamClick,
        setSelectedCollection,
        onLocalAssetClick,
    } = useActions({});

    const isEnabled = isPlatformAllowed([features.EDIT_WITH_AI]);

    if (!isEnabled) {
        return null;
    }

    const getJWT = async () => {
        const testToken = localStorage.getItem('testGenAIToken');

        if (testToken) {
            return testToken;
        }

        return token;
    };

    const hostURL = () => {
        const testHostURL = localStorage.getItem('testGenAIHostURL');

        if (testHostURL) {
            return testHostURL;
        }

        // with trim last slash if exists
        return `https://${portalDomain}`;
    };

    const handleAssetPickerClose = useCallback(() => {
        setAddAssetsCallback(undefined);
    }, []);

    const handleAssetPickerSelect = useCallback(
        (
            [asset],
            metadata?: {
                selectedFile?: {
                    thumbnail?: string;
                    thumbnailUrl?: string;
                    previewUrl?: string;
                    url?: string;
                    thumbnails?: Record<string, string>;
                };
            },
        ) => {
            if (!asset || !addAssetsCallback) {
                handleAssetPickerClose();

                return;
            }

            const rawDatabaseId = asset?.databaseId ?? asset?.id;
            const databaseId = rawDatabaseId ? String(rawDatabaseId) : undefined;

            if (!databaseId) {
                handleAssetPickerClose();

                return;
            }

            const selectedFile = metadata?.selectedFile;

            // Compact View surfaces thumbnails in different fields, prefer the most specific ones.
            const thumbnail =
                selectedFile?.thumbnail ||
                selectedFile?.thumbnailUrl ||
                selectedFile?.previewUrl ||
                selectedFile?.thumbnails?.['320x320'] ||
                selectedFile?.url ||
                asset?.thumbnails?.['320x320'] ||
                asset?.thumbnails?.['1920x1920'] ||
                asset?.thumbnail ||
                asset?.thumbnailUrl ||
                asset?.previewUrls?.[0] ||
                asset?.previewUrl ||
                asset?.originalUrl ||
                '';

            const payload: AssetPickerPayload = [
                {
                    databaseId,
                    thumbnail: thumbnail || undefined,
                },
            ];

            addAssetsCallback(payload);

            handleAssetPickerClose();
        },
        [addAssetsCallback, handleAssetPickerClose],
    );

    const assetPickerCompactViewProps = useMemo(
        () => ({
            language: 'en_US',
            mode: 'SingleSelect',
            assetTypes: ['IMAGE'],
            isContainerMode: true,
            hideSwitch: true,
            selectedAssets: [],
            onSuccess: handleAssetPickerSelect,
        }),
        [handleAssetPickerSelect],
    );

    const handleLocalAssetConfirm = useCallback(async () => {
        if (!addAssetsCallback || !selectedLocalAsset) {
            return;
        }

        setIsLocalAssetLoading(true);

        try {
            const { aiEditData } = await AssetsService.prepareAIEdit(selectedLocalAsset.id);

            const thumbnail =
                selectedLocalAsset.thumbnails?.['320x320'] ||
                Object.values(selectedLocalAsset.thumbnails ?? {})[0] ||
                '';

            const payload: AssetPickerPayload = [
                {
                    fileId: aiEditData.fileId,
                    thumbnail: thumbnail || undefined,
                },
            ];

            addAssetsCallback(payload);
            handleAssetPickerClose();
        } catch {
            notify({
                title: translate('modal.assetsPicker.error.title.generic'),
                variant: 'error',
            });
        } finally {
            setIsLocalAssetLoading(false);
        }
    }, [addAssetsCallback, selectedLocalAsset, handleAssetPickerClose, translate]);

    const isAssetPickerOpen = Boolean(addAssetsCallback);

    return (
        <>
            <GenAIModal
                getJWT={getJWT}
                originalAsset={originalAsset}
                sessionId={sessionId}
                isOpen={isOpen}
                onClose={onClose}
                onSave={onSave}
                allowedTools={['REMOVE_BACKGROUND', 'REPLACE_BACKGROUND', 'RESIZE', 'SMART_EDIT']}
                saveButtonLabel={saveButtonLabel || translate('image-gen.modal.apply-changes')}
                isDownloadAvailable
                hostURL={hostURL()}
                clientName="STUDIO"
                showAssetPicker={(callback) => setAddAssetsCallback(() => callback)}
                closeAssetPicker={() => setAddAssetsCallback(undefined)}
            />

            {isAssetPickerOpen && (
                <ModalBase size="fullscreen" isOpen onClose={handleAssetPickerClose}>
                    <ModalBase.Header
                        title={translate('modal.assetsPicker.button.add_asset')}
                        additionalActions={
                            assetType === AssetGroup.LOCAL ? (
                                <UploadButton
                                    iconType="upload"
                                    assetTypes={['image']}
                                    collectionId={collectionId}
                                    multiple
                                />
                            ) : undefined
                        }
                    />
                    {allowInternalAsset && (
                        <ModalBase.Sidebar>
                            <Sidebar
                                assetType={assetType}
                                type="image"
                                selectedCollectionId={collectionId}
                                setSelectedCollection={setSelectedCollection}
                                handleDamClick={handleDamClick}
                            />
                        </ModalBase.Sidebar>
                    )}
                    <ModalBase.Content>
                        <StyledModalBasedContent isLocal={assetType === AssetGroup.LOCAL} ref={wrapperRef}>
                            <AssetsPicker
                                type="image"
                                collectionId={collectionId}
                                assetType={assetType}
                                selectedItem={selectedLocalAsset}
                                onSelectLocal={onLocalAssetClick}
                                compactViewProps={assetPickerCompactViewProps}
                                isPersonalUploadsAllowed
                                filterTypes={['IMAGE']}
                            />
                            {isLocalAssetLoading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    }}
                                >
                                    <Spinner />
                                </div>
                            )}
                        </StyledModalBasedContent>
                    </ModalBase.Content>
                    {assetType === AssetGroup.LOCAL && selectedLocalAsset && (
                        <ModalBase.Footer
                            actionPrimary={null}
                            actionSecondary={
                                <Button
                                    variant="primary"
                                    isLoading={isLocalAssetLoading}
                                    onClick={handleLocalAssetConfirm}
                                >
                                    {translate('modal.assetsPicker.button.add_asset')}
                                </Button>
                            }
                        />
                    )}
                </ModalBase>
            )}
        </>
    );
}
